// Query descriptors for the jobs page. Freshness mirrors the legacy app:
// jobs staleTime 0 (cached list paints instantly on route re-entry, then
// refreshes), projects staleTime 0, organization pinned for the session.

import type { JobsListFilters, JobsSortField } from '@ionq/trpc-console';
import { infiniteQueryOptions } from '@tanstack/react-query';
import { trpc } from '../trpc';

// Benchmarked sweet spot (2k-row dataset): query time is FLAT across page
// sizes (40 rows ≈ 7.0ms, 250 ≈ 6.7ms — the per-request fixed overhead
// dominates), payload is ~233 bytes/row. 120 rows = ~6 viewports of runway
// for the same latency as 40, keeping skeletons off-screen in normal
// scrolling while the buffer prefetch (JobsTable) tops up ahead.
const JOBS_PAGE_SIZE = 120;

/** Cursor-paginated my-jobs listing. The mapped filters AND the sort
 * (column + direction) are part of the query key (via the trpc input), so
 * changing any swaps to a fresh page-1 fetch — sorting happens server-side
 * over the whole filtered set, never over the loaded pages. */
export const myJobsInfiniteOptions = (
    filters: JobsListFilters,
    order: 'asc' | 'desc',
    sortBy: JobsSortField,
) =>
    infiniteQueryOptions({
        ...trpc.jobs.list.infiniteQueryOptions(
            { filters, limit: JOBS_PAGE_SIZE, order, sortBy },
            {
                getNextPageParam: (lastPage) => lastPage.next,
                initialCursor: null,
            },
        ),
        // An INFINITE query refetches every loaded page sequentially when
        // stale — staleTime 0 + focus refetch meant a 10-page scroll cost 10
        // serial round-trips on every tab refocus. 30s matches the backend's
        // project-label cache horizon; route re-entry within it paints from
        // cache instantly.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });

/** One-shot CSV export (the whole filtered set, or just `ids`) — never
 * cached, so every click downloads fresh data. */
export const jobsExportCsvOptions = (
    filters: JobsListFilters,
    ids?: string[],
) =>
    trpc.jobs.exportCsv.queryOptions(
        { filters, ...(ids && { ids }) },
        { staleTime: 0, gcTime: 0 },
    );

export const projectsQueryOptions = () =>
    trpc.projects.list.queryOptions(undefined, {
        staleTime: 0,
    });

// Session-lifetime cache, like the legacy useOrganization.
export const organizationQueryOptions = () =>
    trpc.organizations.current.queryOptions(undefined, {
        staleTime: Number.POSITIVE_INFINITY,
    });
